// TEMPORARY (temp/linked-customer): shows the link state of one customer, polls while it is pending, and offers
// Confirm (existing Booking Form customer), Retry and Resync (admins) and "Verify 2 more numbers".
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { linkedApi, BASE } from "./linkedApi";
import { pollPlan, stateLabel, stateTone, stateMessage, canRetry, canResync, canVerifyPhones, syncNote, syncIssues } from "./linkedLogic";

const LinkedStatusPanel = ({ customerId, base = BASE, isAdmin = false, onVerifyPhones, initial = null, onChange }) => {
    const api = useRef(linkedApi(base)).current;
    const [info, setInfo] = useState(initial);
    const [hint, setHint] = useState("");
    const [busy, setBusy] = useState(false);
    const [pollKey, setPollKey] = useState(0);
    const alive = useRef(true);

    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

    useEffect(() => {
        let timer;
        const startedAt = Date.now();
        const tick = async () => {
            const r = await api.get(customerId);
            if (!alive.current) return;
            const data = r.ok ? r.data : null;
            if (data) { setInfo(data); onChange?.(data); }
            const plan = pollPlan(data ? data.state : "pending", Date.now() - startedAt);
            setHint(plan.hint);
            if (plan.keepPolling) timer = setTimeout(tick, plan.delayMs);
        };
        tick();
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, pollKey]);

    const act = useCallback(async (fn, okText) => {
        setBusy(true);
        const r = await fn();
        setBusy(false);
        if (!alive.current) return;
        if (r.ok) { toast.success(r.message || okText); setPollKey((k) => k + 1); } else { toast.error(r.message || "Something went wrong"); }
    }, []);

    if (!info) return <div className="text-muted small">Checking Booking Form link...</div>;

    const tone = stateTone(info.state);
    return (
        <div>
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <span className={`badge text-bg-${tone}`}>{stateLabel(info.state)}</span>
                {info.state === "linked" && info.oldId ? <span className="small text-muted">Booking Form ID <strong>{info.oldId}</strong></span> : ""}
            </div>
            <div className="small">{stateMessage(info)}</div>
            {syncNote(info) ? <div className={`small mt-1 ${info.lastSyncError ? "text-danger" : "text-muted"}`}>{syncNote(info)}</div> : ""}
            {syncIssues(info).map((t) => <div key={t} className="small text-warning-emphasis mt-1">{t}</div>)}
            {hint ? <div className="small text-muted mt-2">{hint}</div> : ""}

            {info.state === "confirm_required" && info.existing ? (
                <div className="border rounded-3 p-3 mt-3">
                    <div className="fw-semibold mb-1">Existing Booking Form customer</div>
                    <div className="small">ID <strong>{info.existing.customerId}</strong> · first name <strong>{info.existing.firstName || "-"}</strong></div>
                    <div className="small text-muted">
                        Verified numbers: {(info.existing.phones || []).filter((p) => p.verified).length} of {(info.existing.phones || []).length}
                    </div>
                    <div className="small text-muted mt-1">Only confirm if this is the same person. Existing details in the Booking Form are never overwritten or emptied.</div>
                    <button type="button" className="btn btn-primary btn-sm mt-2" disabled={busy}
                        onClick={() => act(() => api.confirm(customerId, info.existing.customerId), "Confirmed")}>
                        {busy ? "Confirming..." : "Yes, link to this customer"}
                    </button>
                </div>
            ) : ""}

            <div className="d-flex gap-2 flex-wrap mt-3">
                {canVerifyPhones(info) && onVerifyPhones ? <button type="button" className="btn btn-outline-primary btn-sm" onClick={onVerifyPhones}>Verify 2 more numbers</button> : ""}
                {isAdmin && canRetry(info) ? <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => act(() => api.retry(customerId), "Retrying")}>Retry</button> : ""}
                {isAdmin && canResync(info) ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => act(() => api.resync(customerId), "Sync requested")}>Sync now</button> : ""}
                {info.state === "failed" && !isAdmin ? <span className="small text-muted">Ask an admin to retry.</span> : ""}
            </div>
        </div>
    );
};

export default LinkedStatusPanel;
